import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { RegionSetStatusType, SpecialtyTypeEnum } from "@app/projects/enumerators";
import { MultiMapExportAdequacyOutcome, MultiMapExportBaseMap, MultiMapExportBoundaryLayer, MultiMapExportCountyShading, MultiMapExportFileStructure, MultiMapExportMemberLayer, MultiMapExportRouteState, MultiMapExportSolidFillColor, MultiMapExportZoomLevel } from "@app/projects/network-assessment/multi-map-export/interfaces";

export enum MultiMapExportMapType {
  CountySpecialtyDetail = "countySpecialtyDetail",
}

export enum MultiMapExportAction {
  AdequacyFilterChanged = "ADEQUACY_FILTER_CHANGED",
  CountiesChanged = "COUNTIES_CHANGED",
  CountyStatusesChanged = "COUNTY_STATUSES_CHANGED",
  MapContentChanged = "MAP_CONTENT_CHANGED",
  MapStyleChanged = "MAP_STYLE_CHANGED",
  MapTypeChanged = "MAP_TYPE_CHANGED",
  OutputChanged = "OUTPUT_CHANGED",
  ProviderSourcesChanged = "PROVIDER_SOURCES_CHANGED",
  ServiceAreaChanged = "SERVICE_AREA_CHANGED",
  SpecialtiesChanged = "SPECIALTIES_CHANGED",
  SpecialtyGroupChanged = "SPECIALTY_GROUP_CHANGED",
  SpecialtyTypesChanged = "SPECIALTY_TYPES_CHANGED",
}

/** The Failing outcomes arrive selected and Passing arrives cleared. */
export const DEFAULT_ADEQUACY_OUTCOMES: readonly MultiMapExportAdequacyOutcome[] = Object.freeze([
  MultiMapExportAdequacyOutcome.FailingServicingRequirement,
  MultiMapExportAdequacyOutcome.FailingAccessRequirement,
  MultiMapExportAdequacyOutcome.FailingBothRequirements,
]);

// Map Content defaults are fixed by the story, deliberately independent of the map the user came from,
// so unlike the criteria sections they are seeded here rather than reconciled against the launch context.
export const DEFAULT_MAP_CONTENT_MEMBER_LAYERS: readonly MultiMapExportMemberLayer[] =
  Object.freeze([MultiMapExportMemberLayer.WithAccess, MultiMapExportMemberLayer.WithoutAccess]);

/** The only provider option checked on arrival, for every user. */
export const DEFAULT_MAP_CONTENT_PROVIDER_OPTIONS: readonly ProvidersFilterOptions[] =
  Object.freeze([ProvidersFilterOptions.NonTerminated]);

export const DEFAULT_MAP_STYLE_BOUNDARY_LAYERS: readonly MultiMapExportBoundaryLayer[] =
  Object.freeze([MultiMapExportBoundaryLayer.CountyBoundaries]);

export const DEFAULT_MAP_STYLE_BASE_MAP = MultiMapExportBaseMap.Light;

export const DEFAULT_MAP_STYLE_COUNTY_SHADING = MultiMapExportCountyShading.SolidColor;

export const DEFAULT_MAP_STYLE_SOLID_FILL_COLOR = MultiMapExportSolidFillColor.White;

export const DEFAULT_MAP_STYLE_ZOOM_LEVEL = MultiMapExportZoomLevel.MapContents;

export const DEFAULT_MAP_STYLE_SHOW_SCALE_BAR = false;

export const DEFAULT_OUTPUT_EMAIL_WHEN_READY = true;

export const DEFAULT_OUTPUT_FILE_STRUCTURE = MultiMapExportFileStructure.IndividualJpegs;

export const DEFAULT_OUTPUT_INCLUDE_PAGE_HEADER = true;

/** The field starts empty; "Enter a name for the export" is a placeholder, never a value. */
export const DEFAULT_OUTPUT_REPORT_NAME = "";

export const DEFAULT_OUTPUT_SHARED_USERS: readonly number[] = Object.freeze([]);

export type MultiMapExportActions =
  { type: MultiMapExportAction, value: MultiMapExportState };

export interface MultiMapExportState {
  readonly emailWhenReady?: boolean;
  readonly includePageHeader?: boolean;
  readonly reportName?: string;
  readonly selectedAdequacyOutcomes?: readonly MultiMapExportAdequacyOutcome[];
  readonly selectedBaseMap?: MultiMapExportBaseMap;
  readonly selectedBoundaryLayers?: readonly MultiMapExportBoundaryLayer[];
  readonly selectedCountyRegionSetIds?: readonly number[];
  readonly selectedCountyShading?: MultiMapExportCountyShading;
  /** The same counties as `selectedCountyRegionSetIds`, as the SSA codes the export filters on. */
  readonly selectedCountySsas?: readonly string[];
  readonly selectedCountyStatuses?: readonly RegionSetStatusType[];
  readonly selectedFileStructure?: MultiMapExportFileStructure;
  readonly selectedMapContentMemberLayers?: readonly MultiMapExportMemberLayer[];
  /** Leaf options only. The parent checkboxes are derived, never stored. */
  readonly selectedMapContentProviderOptions?: readonly ProvidersFilterOptions[];
  readonly selectedMapType?: MultiMapExportMapType;
  readonly selectedProviderSources?: readonly ProviderNetworkTypeEnum[];
  readonly selectedServiceArea?: string;
  readonly selectedServiceAreaCountyCode?: string;
  readonly selectedServiceAreaStateCode?: string;
  readonly selectedSolidFillColor?: MultiMapExportSolidFillColor;
  readonly selectedSpecialtyGroupCode?: string;
  readonly selectedSpecialtyGroupCodes?: readonly string[];
  readonly selectedSpecialtyGroupName?: string;
  readonly selectedSpecialtyTypes?: readonly SpecialtyTypeEnum[];
  readonly selectedZoomLevel?: MultiMapExportZoomLevel;
  readonly sharedUsers?: readonly number[];
  readonly showScaleBar?: boolean;
}

const frozenCopy = <T>(values?: readonly T[]): readonly T[] | undefined =>
  values === undefined ? undefined : Object.freeze([...values]);

export const multiMapExportInitialState = (launchContext?: MultiMapExportRouteState): MultiMapExportState => Object.freeze({
  emailWhenReady: DEFAULT_OUTPUT_EMAIL_WHEN_READY,
  includePageHeader: DEFAULT_OUTPUT_INCLUDE_PAGE_HEADER,
  reportName: DEFAULT_OUTPUT_REPORT_NAME,
  selectedAdequacyOutcomes: DEFAULT_ADEQUACY_OUTCOMES,
  selectedBaseMap: DEFAULT_MAP_STYLE_BASE_MAP,
  selectedBoundaryLayers: DEFAULT_MAP_STYLE_BOUNDARY_LAYERS,
  // The Service Area section sets these once its counties load; the launch context does not carry them.
  selectedCountyRegionSetIds: undefined,
  selectedCountyShading: DEFAULT_MAP_STYLE_COUNTY_SHADING,
  selectedCountySsas: undefined,
  selectedCountyStatuses: frozenCopy(launchContext?.selectedCountyStatuses),
  selectedFileStructure: DEFAULT_OUTPUT_FILE_STRUCTURE,
  // Already frozen module constants, so they are shared rather than copied.
  selectedMapContentMemberLayers: DEFAULT_MAP_CONTENT_MEMBER_LAYERS,
  selectedMapContentProviderOptions: DEFAULT_MAP_CONTENT_PROVIDER_OPTIONS,
  selectedMapType: MultiMapExportMapType.CountySpecialtyDetail,
  selectedProviderSources: frozenCopy(launchContext?.selectedProviderSources),
  selectedServiceArea: launchContext?.selectedServiceArea,
  selectedServiceAreaCountyCode: launchContext?.selectedServiceAreaCountyCode,
  selectedServiceAreaStateCode: launchContext?.selectedServiceAreaStateCode,
  selectedSolidFillColor: DEFAULT_MAP_STYLE_SOLID_FILL_COLOR,
  selectedSpecialtyGroupCode: launchContext?.selectedSpecialtyGroupCode,
  // The Specialty section sets these once its specialty groups load; the launch context carries no type.
  selectedSpecialtyGroupCodes: undefined,
  selectedSpecialtyGroupName: launchContext?.selectedSpecialtyGroupName,
  selectedSpecialtyTypes: undefined,
  selectedZoomLevel: DEFAULT_MAP_STYLE_ZOOM_LEVEL,
  sharedUsers: DEFAULT_OUTPUT_SHARED_USERS,
  showScaleBar: DEFAULT_MAP_STYLE_SHOW_SCALE_BAR,
});

export const multiMapExportReducer = (state: MultiMapExportState, action: MultiMapExportActions): MultiMapExportState => {
  switch (action.type) {
    // An empty array is a real value — the user cleared every outcome — so it is stored rather than
    // falling back to the previous selection.
    case MultiMapExportAction.AdequacyFilterChanged:
      return Object.freeze({
        ...state,
        selectedAdequacyOutcomes: frozenCopy(action.value.selectedAdequacyOutcomes),
      });
    // Ids and SSAs are always written together from one snapshot, so they cannot drift apart.
    case MultiMapExportAction.CountiesChanged:
      return Object.freeze({
        ...state,
        selectedCountyRegionSetIds: frozenCopy(action.value.selectedCountyRegionSetIds),
        selectedCountySsas: frozenCopy(action.value.selectedCountySsas),
      });
    // Statuses and counties move together: changing the statuses changes which counties are in
    // scope, so the caller reconciles both and dispatches them as one update.
    case MultiMapExportAction.CountyStatusesChanged:
      return Object.freeze({
        ...state,
        selectedCountyRegionSetIds: frozenCopy(action.value.selectedCountyRegionSetIds),
        selectedCountySsas: frozenCopy(action.value.selectedCountySsas),
        selectedCountyStatuses: frozenCopy(action.value.selectedCountyStatuses),
      });
    // Members and providers are one control group, so the section dispatches whichever half changed and
    // the other is preserved. An empty array is a real value — the user cleared everything — and is kept.
    case MultiMapExportAction.MapContentChanged:
      return Object.freeze({
        ...state,
        selectedMapContentMemberLayers: frozenCopy(action.value.selectedMapContentMemberLayers) ?? state.selectedMapContentMemberLayers,
        selectedMapContentProviderOptions: frozenCopy(action.value.selectedMapContentProviderOptions) ?? state.selectedMapContentProviderOptions,
      });
    case MultiMapExportAction.MapStyleChanged:
      return Object.freeze({
        ...state,
        selectedBaseMap: action.value.selectedBaseMap ?? state.selectedBaseMap,
        selectedBoundaryLayers: frozenCopy(action.value.selectedBoundaryLayers) ?? state.selectedBoundaryLayers,
        selectedCountyShading: action.value.selectedCountyShading ?? state.selectedCountyShading,
        selectedSolidFillColor: action.value.selectedSolidFillColor ?? state.selectedSolidFillColor,
        selectedZoomLevel: action.value.selectedZoomLevel ?? state.selectedZoomLevel,
        showScaleBar: action.value.showScaleBar ?? state.showScaleBar,
      });
    case MultiMapExportAction.MapTypeChanged:
      return Object.freeze({
        ...state,
        selectedMapType: action.value.selectedMapType,
      });
    // ?? not ||: an empty report name must clear, not fall back.
    case MultiMapExportAction.OutputChanged:
      return Object.freeze({
        ...state,
        emailWhenReady: action.value.emailWhenReady ?? state.emailWhenReady,
        includePageHeader: action.value.includePageHeader ?? state.includePageHeader,
        reportName: action.value.reportName ?? state.reportName,
        selectedFileStructure: action.value.selectedFileStructure ?? state.selectedFileStructure,
        sharedUsers: frozenCopy(action.value.sharedUsers) ?? state.sharedUsers,
      });
    case MultiMapExportAction.ProviderSourcesChanged:
      return Object.freeze({
        ...state,
        selectedProviderSources: frozenCopy(action.value.selectedProviderSources),
      });
    case MultiMapExportAction.ServiceAreaChanged:
      return Object.freeze({
        ...state,
        selectedServiceArea: action.value.selectedServiceArea,
        selectedServiceAreaCountyCode: action.value.selectedServiceAreaCountyCode,
        selectedServiceAreaStateCode: action.value.selectedServiceAreaStateCode,
      });
    case MultiMapExportAction.SpecialtiesChanged:
      return Object.freeze({
        ...state,
        selectedSpecialtyGroupCodes: frozenCopy(action.value.selectedSpecialtyGroupCodes),
      });
    case MultiMapExportAction.SpecialtyGroupChanged:
      return Object.freeze({
        ...state,
        selectedSpecialtyGroupCode: action.value.selectedSpecialtyGroupCode,
        selectedSpecialtyGroupName: action.value.selectedSpecialtyGroupName,
      });
    // Types and specialties move together: changing the types changes which specialties are in
    // scope, so the caller reconciles both and dispatches them as one update.
    case MultiMapExportAction.SpecialtyTypesChanged:
      return Object.freeze({
        ...state,
        selectedSpecialtyGroupCodes: frozenCopy(action.value.selectedSpecialtyGroupCodes),
        selectedSpecialtyTypes: frozenCopy(action.value.selectedSpecialtyTypes),
      });
    default:
      return state;
  }
};
